import { browser, dev } from '$app/environment';
// import { appConfig } from './config/features';
// import { version } from '../../package.json';

export const APP_NAME = 'NEBUL PrivateGPT';

export const WEBUI_HOSTNAME = browser ? (dev ? `${location.hostname}:8080` : ``) : '';
export const WEBUI_BASE_URL = browser ? (dev ? `http://${WEBUI_HOSTNAME}` : ``) : ``;
export const WEBUI_API_BASE_URL = `${WEBUI_BASE_URL}/api/v1`;
export const WEBUI_API_V2_BASE_URL = `${WEBUI_BASE_URL}/api/v2`;

export const OLLAMA_API_BASE_URL = `${WEBUI_BASE_URL}/ollama`;
export const OPENAI_API_BASE_URL = `${WEBUI_BASE_URL}/openai`;
export const AUDIO_API_BASE_URL = `${WEBUI_BASE_URL}/audio/api/v1`;
export const IMAGES_API_BASE_URL = `${WEBUI_BASE_URL}/images/api/v1`;
export const RETRIEVAL_API_BASE_URL = `${WEBUI_BASE_URL}/retrieval/api/v1`;

// @ts-ignore - These are defined in vite.config.ts as environment variables
export const WEBUI_VERSION = typeof APP_VERSION !== 'undefined' ? APP_VERSION : 'dev';
// @ts-ignore - These are defined in vite.config.ts as environment variables
export const WEBUI_BUILD_HASH =
	typeof APP_BUILD_HASH !== 'undefined' ? APP_BUILD_HASH : 'dev-build';
export const REQUIRED_OLLAMA_VERSION = '0.1.16';

export const SUPPORTED_FILE_TYPE = [
	'application/epub+zip',
	'application/pdf',
	'text/plain',
	'text/csv',
	'text/xml',
	'text/html',
	'text/x-python',
	'text/css',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/octet-stream',
	'application/x-javascript',
	'text/markdown',
	'audio/mpeg',
	'audio/wav',
	'audio/ogg',
	'audio/x-m4a'
];

export const SUPPORTED_FILE_EXTENSIONS = [
	'md',
	'rst',
	'go',
	'py',
	'java',
	'sh',
	'bat',
	'ps1',
	'cmd',
	'js',
	'ts',
	'css',
	'cpp',
	'hpp',
	'h',
	'c',
	'cs',
	'htm',
	'html',
	'sql',
	'log',
	'ini',
	'pl',
	'pm',
	'r',
	'dart',
	'dockerfile',
	'env',
	'php',
	'hs',
	'hsc',
	'lua',
	'nginxconf',
	'conf',
	'm',
	'mm',
	'plsql',
	'perl',
	'rb',
	'rs',
	'db2',
	'scala',
	'bash',
	'swift',
	'vue',
	'svelte',
	'doc',
	'docx',
	'pdf',
	'csv',
	'txt',
	'xls',
	'xlsx',
	'pptx',
	'ppt',
	'msg'
];

export const PASTED_TEXT_CHARACTER_LIMIT = 1000;

import type { AccessLevel } from '$lib/types/accessControl';
import {
	Crown,
	Eye,
	EyeOff,
	FileIcon,
	FileJsonIcon,
	FileTextIcon,
	Folder,
	Globe,
	ImageIcon,
	Shield,
	Lock,
	Pencil,
	Plus,
	Settings,
	TicketX,
	Trash,
	TriangleAlert,
	User,
	UsersRound,
	Search,
	MessageSquareQuote,
	PersonStanding
} from 'lucide-svelte';
import {
	GlobeAlt,
	LockClosed,
	UsersSolid,
	Building,
	Document,
	WrenchSolid
} from './components/icons/icons';
import YouTube from './components/icons/YouTube.svelte';
import Spinner from 'flowbite-svelte/Spinner.svelte';
import type { ComponentType } from 'svelte';
import { ItemType } from './apis/v2/items';
import type { Knowledge } from './apis/v2/knowledge';
import type { Persona } from './apis/v2/personas';
import type { Prompt } from './apis/v2/prompts';

export const ACCESS_CONTROL_ICONS: Record<AccessLevel, typeof GlobeAlt> = {
	public: GlobeAlt,
	private: LockClosed,
	restricted: UsersSolid,
	internal: Building,
	document: Document,
	admin: WrenchSolid
};

export const USER_ROLES = {
	admin: 'admin',
	user: 'user'
};

export const USER_ROLES_ICONS: Record<keyof typeof USER_ROLES, typeof Shield> = {
	admin: Shield,
	user: User
};

type IconConfig = { icon: ComponentType; bgClassName: string; textColor: string };

// Strict mapping for *only* ItemType values
const ITEM_TYPE_ICONS: Record<ItemType, IconConfig> = {
	[ItemType.FILE]: { icon: FileIcon, bgClassName: 'bg-file', textColor: 'text-file' },
	[ItemType.JSON_OBJECT]: {
		icon: FileJsonIcon,
		bgClassName: 'bg-knowledge',
		textColor: 'text-knowledge'
	},
	[ItemType.TEXT]: { icon: FileTextIcon, bgClassName: 'bg-knowledge', textColor: 'text-knowledge' },
	[ItemType.TOPDESK_KNOWLEDGE_ITEM]: {
		icon: TicketX,
		bgClassName: 'bg-topdesk',
		textColor: 'text-topdesk'
	},
	[ItemType.IMAGE]: { icon: ImageIcon, bgClassName: 'bg-image', textColor: 'text-image' },
	[ItemType.TOPDESK_INCIDENT]: {
		icon: TriangleAlert,
		bgClassName: 'bg-topdesk',
		textColor: 'text-topdesk'
	},
	[ItemType.YOUTUBE]: { icon: YouTube, bgClassName: 'bg-red-500', textColor: 'text-red-500' }
};

// Combined export with extras
export const FILES_ICONS = {
	...ITEM_TYPE_ICONS,
	['knowledge']: { icon: Folder, bgClassName: 'bg-knowledge', textColor: 'text-knowledge' },
	['spinner']: { icon: Spinner, bgClassName: 'bg-muted', textColor: 'text-muted-foreground' } // loading state
} as const;

export const TEMPLATE_ROLES = [
	{
		value: 'none',
		label: 'None',
		icon: EyeOff,
		permissions: [],
		description: 'No access to view or manage the resource.'
	},
	{
		value: 'viewer',
		label: 'Viewer',
		icon: Eye,
		permissions: ['read'],
		description: 'Can only view the resource without making changes.'
	},
	{
		value: 'editor',
		label: 'Editor',
		icon: Pencil,
		permissions: ['create', 'read', 'update'],
		description: 'Can view, add, and update existing the resource.'
	},
	{
		value: 'admin',
		label: 'Administrator',
		icon: Crown,
		permissions: ['create', 'read', 'update', 'delete'],
		description: 'Full control: can view, add, edit, and delete the resource.'
	}
] as const;

export type RoleValue = (typeof TEMPLATE_ROLES)[number]['value'];

export const AVAILABLE_RELATIONS = [
	{ value: 'read', label: 'Read', icon: Eye },
	{ value: 'create', label: 'Create', icon: Plus },
	{ value: 'update', label: 'Update', icon: Pencil },
	{ value: 'delete', label: 'Delete', icon: Trash }
];

// Source: https://kit.svelte.dev/docs/modules#$env-static-public
// This feature, akin to $env/static/private, exclusively incorporates environment variables
// that are prefixed with config.kit.env.publicPrefix (usually set to PUBLIC_).
// Consequently, these variables can be securely exposed to client-side code.

// ---------------------------------------------
// API Route Metadata (for admin route access UI)
// ---------------------------------------------
export type ApiRouteMeta = {
	key: string;
	title: string;
	description: string;
	icon: ComponentType;
	apiBasePath: string;
};

export type ResourceType = keyof typeof WORKSPACE_RESOURCES;

export const WORKSPACE_RESOURCES = {
	knowledge: {
		type: 'knowledge',
		title: 'Knowledge Bases',
		icon: FILES_ICONS.knowledge.icon,
		workspacePermission: { namespace: 'privategpt', object: 'knowledge', relation: 'read' },
		getTitle: (item: Knowledge) => item.name,
		getDescription: (item: Knowledge, i18n: any) => item.description || '',
		noAccessMessage: 'The target has no access to any knowledge'
	},
	personas: {
		type: 'personas',
		title: 'Personas',
		icon: PersonStanding,
		workspacePermission: { namespace: 'privategpt', object: 'personas', relation: 'read' },
		getTitle: (item: Persona) => item.name,
		getDescription: (item: Persona, i18n: any) => `${i18n.t('Persona ID')}: ${item.id}`,
		noAccessMessage: 'The target has no access to any personas'
	},
	prompts: {
		type: 'prompts',
		title: 'Prompts',
		icon: MessageSquareQuote,
		workspacePermission: { namespace: 'privategpt', object: 'prompts', relation: 'read' },
		getTitle: (item: Prompt) => item.title,
		getDescription: (item: Prompt, i18n: any) => item.command,
		noAccessMessage: 'The target has no access to any prompts'
	}
} as const;

// Key must match the first segment after /api/v2/ (lowercase).
export const API_RESOURCES_META: Record<string, ApiRouteMeta> = {
	knowledge: {
		key: 'knowledge',
		title: 'Knowledge Bases',
		description:
			'Grants access to the Knowledge Bases section. Users can create and manage their own knowledge bases, and only see other knowledge bases if explicitly granted access.',
		icon: WORKSPACE_RESOURCES.knowledge.icon,
		apiBasePath: '/api/v2/knowledge'
	},
	personas: {
		key: 'personas',
		title: 'Personas',
		description:
			'Grants access to the Personas section. Users can create and manage their own personas, and only see other personas if explicitly granted access.',
		icon: WORKSPACE_RESOURCES.personas.icon,
		apiBasePath: '/api/v2/personas'
	},
	prompts: {
		key: 'prompts',
		title: 'Prompts',
		description:
			'Grants access to the Prompts section. Users can create and manage their own prompts, and only see other prompts if explicitly granted access.',
		icon: WORKSPACE_RESOURCES.prompts.icon,
		apiBasePath: '/api/v2/prompts'
	},
	integrations: {
		key: 'integrations',
		title: 'Integrations',
		description:
			'Grants access to the Integrations section. Users can configure and manage integrations available to them within the workspace.',
		icon: Globe,
		apiBasePath: '/api/v2/integrations'
	}
};
